import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { removalCountLabels } from "hooks/queries/user";
import { translate, translateWithVars } from "shared/languages";
import { RegisterClassForm } from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import client from "shared/utils/axios-config";

const useCreateClassType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (registerForm: RegisterClassForm) => {
      return client.post(`/v1/class/`, registerForm);
    },
    onSuccess: ({ data: response }) => {
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`classtype`] });
      getApiSuccessMessage(`${translate("messages.success.new_class")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

const useUpdateClassType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ registerForm, classId }: { classId: string; registerForm: RegisterClassForm }) => {
      return client.put(`/v1/class/${classId}`, registerForm);
    },
    onSuccess: ({ data: response }) => {
      const class_id = response.data.id;
      // Invalidate the single-class detail AND the class list. The create-
      // schedule drawer reads class duration/max_participants from the list
      // query (getAllClassTypeByBusinessId, staleTime 5min), so without the
      // list invalidation it keeps showing/using stale values until a hard
      // refresh. (create/delete already invalidate the list.)
      queryClient.invalidateQueries({ queryKey: [`classtype_id`, class_id] });
      queryClient.invalidateQueries({ queryKey: [`classtype`] });
      // The class's credit cost is read through the product (Detail Kelas's
      // Paket tab, the product form) and the booking pickers' cost suffix.
      queryClient.invalidateQueries({ queryKey: ["product"] });
      queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      getApiSuccessMessage(`${translate("label.class_information")} ${translate("messages.success.update")}`);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// removeUpcoming deletes every upcoming session of the class first, in the
// same request. onRefused runs when the server still finds one (added in the
// meantime), so the pop-up can refresh its list.
const useDeleteClassType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      classId,
      removeUpcoming,
    }: {
      classId: string;
      removeUpcoming: boolean;
      onRefused?: () => void;
    }) => {
      return client.delete(`/v1/class/${classId}`, {
        params: removeUpcoming ? { remove_upcoming_classes: true } : undefined,
      });
    },
    onSuccess: ({ data: response }) => {
      // Invalidate all queries based on their access type
      queryClient.invalidateQueries({ queryKey: [`classtype`] });
      queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
      const removal = response?.data?.removal as
        | { removed_classes: number; refunded_bookings: number }
        | null
        | undefined;
      getApiSuccessMessage(
        removal
          ? translateWithVars(
              "class_delete.removed_toast",
              removalCountLabels(removal.removed_classes, removal.refunded_bookings),
            )
          : translate("class_delete.deleted_toast"),
      );
    },
    onError: (error: AxiosError<ErrorResponse>, variables) => {
      getApiErrorMessage(error);
      if (error.response?.status === 422) variables.onRefused?.();
    },
  });
};

export { useCreateClassType, useUpdateClassType, useDeleteClassType };
